import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { BrgButton, ButtonVariant, Colors, FlexBox, MagicBoxRow, useToasts } from '@brg/ui'
import { API, Project, TeamAPI } from '@brg/api'
import { LayoutConst, useModal } from 'core'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import {
  fetchTeamTabProjectsForOrder,
  fetchTeamTabProjectsPageForOrder,
  OrganizationsListSource,
  TEAM_TAB_PROJECTS_ORDER_PAGE_LIMIT,
  TeamTabProjectsPagePaging,
} from 'shared/pages/teams/helpers/fetchTeamTabProjectsForOrder'
import { OrganizationsOrderGetListParams } from 'shared/pages/teams/helpers/buildOrganizationsOrderModalGetListParams'
import { applyCustomSortingToTeamUpdate } from 'shared/pages/teams/helpers/customSortingStorage'
import { syncCustomSorting } from 'shared/pages/teams/helpers/syncCustomSorting'
import { isOrganizationsEntityType } from 'shared/pages/teams/helpers/prepareCustomSortingGridFilter'
import {
  TeamTabProjectsOrderGrid,
  TeamTabProjectsOrderGridSkeleton,
} from 'shared/modals/teamTabProjectsOrder/TeamTabProjectsOrderGrid'
import { ProjectMetaBack } from 'shared/components/ProjectMetaBack/ProjectMetaBack'
import { TabPanelContainer, TabPanelHeader } from 'shared/pages/teams/components/Panel/styles'
import { SwitcherItem } from 'shared/components/ChannelsInfo/styles'
// import { useSortableDragAutoScroll } from 'shared/modals/teamTabProjectsOrder/useSortableDragAutoScroll'

type TeamTabProjectsOrderModalProps = {
  teamId: string
  contextNames: string[]
  organizationsGetListParams?: OrganizationsOrderGetListParams | null
  organizationsListSource?: OrganizationsListSource
  entityType?: string
  projectIds?: string[]
  tabPath?: string
  tabIndex?: number
  isTabFilter?: boolean
  tabName?: string
  organizationsCatalogMode?: boolean
  onSave: (orderedProjectIds: string[]) => void
}

type ModalViewTab = 'all' | 'selected'

const SCROLL_AREA_ID = 'team-tab-projects-order-scroll'
const TEAM_TAB_PROJECTS_ORDER_MAX_SELECTED = 60

const PageWrapper = styled.div`
  width: 100%;
  height: 100vh;
  background: #fefefe;
  padding: 40px 24px 24px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: ${LayoutConst.screen.s - 1}px) {
    padding: 24px 10px 16px;
  }
`

const ContentWrapper = styled.div`
  width: 100%;
  max-width: ${LayoutConst.container.l}px;
  margin: 0 auto;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`

const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  margin-top: 24px;
`

const TabsRow = styled(FlexBox)`
  margin-top: 24px;
  margin-bottom: 8px;
`

const EmptyState = styled.div`
  margin-top: 48px;
  font-size: 17px;
  line-height: 22px;
  color: ${Colors.galBlack47};
`

const LoadMoreSentinel = styled.div`
  width: 100%;
  height: 1px;
`

/** Модалка сортировки проектов вкладки. */
export const TeamTabProjectsOrderModal: FC<TeamTabProjectsOrderModalProps> = ({
  teamId,
  contextNames,
  organizationsGetListParams,
  organizationsListSource,
  entityType,
  projectIds = [],
  tabPath = '',
  tabIndex = 0,
  isTabFilter = true,
  tabName,
  organizationsCatalogMode = false,
  onSave,
}) => {
  const modal = useModal()
  const toasts = useToasts()
  const { t, i18n } = useTranslation()
  const language = i18n.language || 'ru'

  const [activeTab, setActiveTab] = useState<ModalViewTab>('all')
  const [projects, setProjects] = useState<Project[]>([])
  const [selectedProjectsLoaded, setSelectedProjectsLoaded] = useState<Project[]>([])
  const [selectedProjectIds, setSelectedProjectIds] = useState<Set<string>>(() => new Set())
  const [orderedSelectedIds, setOrderedSelectedIds] = useState<string[]>(() => [])
  const [effectiveProjectIds, setEffectiveProjectIds] = useState<string[]>(() => projectIds)
  const [paging, setPaging] = useState<TeamTabProjectsPagePaging | null>(null)
  const [hasReachedEnd, setHasReachedEnd] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [saving, setSaving] = useState(false)
  const loadMoreLockRef = useRef(false)
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null)
  const projectsCountRef = useRef(0)
  const isOrganizations = isOrganizationsEntityType(entityType)

  const contextNamesRef = useRef(contextNames)
  contextNamesRef.current = contextNames

  const organizationsGetListParamsRef = useRef(organizationsGetListParams)
  organizationsGetListParamsRef.current = organizationsGetListParams

  const organizationsListSourceRef = useRef(organizationsListSource)
  organizationsListSourceRef.current = organizationsListSource

  const toastsRef = useRef(toasts)
  toastsRef.current = toasts

  const listQueryKey = useMemo(() => {
    if (isOrganizations) {
      if (organizationsListSource === 'myAdminTeams') {
        return 'myAdminTeams'
      }
      const params = organizationsGetListParams
      if (!params?.contexts?.length) return ''
      return JSON.stringify({
        contexts: params.contexts,
        excludeModerationTypes: params.excludeModerationTypes,
      })
    }
    if (contextNames.length) {
      return contextNames.join(',')
    }
    if (teamId) {
      return `team:${teamId}`
    }
    return ''
  }, [
    isOrganizations,
    organizationsListSource,
    contextNames.join(','),
    teamId,
    organizationsGetListParams?.contexts?.join(','),
    organizationsGetListParams?.excludeModerationTypes?.join(','),
  ])

  useEffect(() => {
    setActiveTab('all')
  }, [tabPath, tabIndex, teamId])

  const title = useMemo(() => {
    const name = tabName || t('teams.adminPage.buttons.empty')
    if (isOrganizations) {
      return `Сортировка организаций на вкладке «${name}»`
    }
    return `Сортировка проектов на вкладке «${name}»`
  }, [tabName, t, isOrganizations])

  const projectsMap = useMemo(() => {
    const map = new Map<string, Project>()

    selectedProjectsLoaded.forEach((project) => {
      map.set(project.projectId, project)
    })

    projects.forEach((project) => {
      if (!map.has(project.projectId)) {
        map.set(project.projectId, project)
      }
    })

    return map
  }, [selectedProjectsLoaded, projects])

  const selectedProjects = useMemo(
    () => orderedSelectedIds
      .filter((id) => selectedProjectIds.has(id))
      .map((id) => projectsMap.get(id))
      .filter(Boolean) as Project[],
    [orderedSelectedIds, selectedProjectIds, projectsMap],
  )

  // const isDragSortActive = activeTab === 'selected' && selectedProjects.length > 0
  // useSortableDragAutoScroll(isDragSortActive ? SCROLL_AREA_ID : undefined)

  const hasMoreProjects = useMemo(() => {
    if (hasReachedEnd || loading || !paging) return false
    if (paging.pages <= 0) return false
    return paging.page < paging.pages
  }, [hasReachedEnd, loading, paging])

  useEffect(() => {
    projectsCountRef.current = projects.length
  }, [projects.length])

  const projectIdsKey = (projectIds || []).filter(Boolean).join(',')

  useEffect(() => {
    const ids = (projectIds || []).filter(Boolean)
    setEffectiveProjectIds(ids)
    setOrderedSelectedIds(ids)
    setSelectedProjectIds(new Set(ids))
  }, [projectIdsKey, tabPath, tabIndex])

  useEffect(() => {
    if (!listQueryKey) {
      setProjects([])
      setPaging(null)
      setSelectedProjectsLoaded([])
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)
    setProjects([])
    setPaging(null)
    setHasReachedEnd(false)
    loadMoreLockRef.current = false

    fetchTeamTabProjectsPageForOrder({
      contextNames: contextNamesRef.current,
      teamId,
      organizationsGetListParams: organizationsGetListParamsRef.current ?? undefined,
      organizationsListSource: organizationsListSourceRef.current,
      entityType,
      language,
      limit: TEAM_TAB_PROJECTS_ORDER_PAGE_LIMIT,
      offset: 0,
    })
      .then(({ projects: loadedProjects, paging: loadedPaging }) => {
        if (cancelled) return
        setProjects(loadedProjects)
        setPaging(loadedPaging)
        if (
          loadedProjects.length === 0
          || loadedProjects.length < TEAM_TAB_PROJECTS_ORDER_PAGE_LIMIT
          || loadedPaging.page >= loadedPaging.pages
        ) {
          setHasReachedEnd(true)
        }
      })
      .catch(() => {
        if (!cancelled) {
          toastsRef.current.error('Не удалось загрузить проекты')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [listQueryKey, entityType, language])

  const loadMoreProjects = useCallback(async () => {
    if (
      loadMoreLockRef.current
      || !listQueryKey
      || loading
      || loadingMore
      || hasReachedEnd
      || !paging
      || paging.page >= paging.pages
    ) {
      return
    }

    loadMoreLockRef.current = true
    setLoadingMore(true)

    try {
      const offset = projectsCountRef.current
      const { projects: loadedProjects, paging: loadedPaging } = await fetchTeamTabProjectsPageForOrder({
        contextNames: contextNamesRef.current,
        teamId,
        organizationsGetListParams: organizationsGetListParamsRef.current ?? undefined,
        organizationsListSource: organizationsListSourceRef.current,
        entityType,
        language,
        limit: TEAM_TAB_PROJECTS_ORDER_PAGE_LIMIT,
        offset,
      })

      if (!loadedProjects.length) {
        setHasReachedEnd(true)
        return
      }

      setProjects((prev) => [...prev, ...loadedProjects])
      setPaging(loadedPaging)

      if (
        loadedProjects.length < TEAM_TAB_PROJECTS_ORDER_PAGE_LIMIT
        || loadedPaging.page >= loadedPaging.pages
      ) {
        setHasReachedEnd(true)
      }
    } catch {
      toastsRef.current.error('Не удалось загрузить проекты')
    } finally {
      loadMoreLockRef.current = false
      setLoadingMore(false)
    }
  }, [listQueryKey, entityType, language, loading, loadingMore, hasReachedEnd, paging])

  const loadMoreProjectsRef = useRef(loadMoreProjects)
  loadMoreProjectsRef.current = loadMoreProjects

  useEffect(() => {
    if (activeTab !== 'all' || !hasMoreProjects || loading || loadingMore || !projects.length) return

    const scrollRoot = document.getElementById(SCROLL_AREA_ID)
    const sentinel = loadMoreSentinelRef.current
    if (!scrollRoot || !sentinel) return

    if (scrollRoot.scrollHeight <= scrollRoot.clientHeight + 50) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMoreProjectsRef.current()
        }
      },
      { root: scrollRoot, rootMargin: '80px', threshold: 0 },
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [activeTab, hasMoreProjects, loading, loadingMore, projects.length])

  const effectiveProjectIdsKey = effectiveProjectIds.join(',')

  useEffect(() => {
    if (!listQueryKey) {
      setSelectedProjectsLoaded([])
      return
    }

    if (!effectiveProjectIds.length) {
      setSelectedProjectsLoaded([])
      return
    }

    let cancelled = false

    fetchTeamTabProjectsForOrder({
      contextNames: contextNamesRef.current,
      teamId,
      organizationsGetListParams: organizationsGetListParamsRef.current ?? undefined,
      organizationsListSource: organizationsListSourceRef.current,
      entityType,
      projectIds: effectiveProjectIds,
      language,
    })
      .then((loadedProjects) => {
        if (cancelled) return
        setSelectedProjectsLoaded(loadedProjects)
      })
      .catch(() => {
        if (!cancelled) setSelectedProjectsLoaded([])
      })

    return () => {
      cancelled = true
    }
  }, [listQueryKey, entityType, language, effectiveProjectIdsKey])

  const onSelectedProjectSort = useCallback((projectId: string, newIndex: number) => {
    setOrderedSelectedIds((prev) => {
      const visibleIds = prev.filter((id) => selectedProjectIds.has(id))
      const oldIndex = visibleIds.indexOf(projectId)
      if (oldIndex === -1 || oldIndex === newIndex) return prev

      const nextVisibleIds = [...visibleIds]
      const [moved] = nextVisibleIds.splice(oldIndex, 1)
      nextVisibleIds.splice(newIndex, 0, moved)

      const visibleSet = new Set(visibleIds)
      const next = [...prev]
      let visiblePointer = 0

      for (let index = 0; index < next.length; index += 1) {
        if (visibleSet.has(next[index])) {
          next[index] = nextVisibleIds[visiblePointer]
          visiblePointer += 1
        }
      }

      return next
    })
  }, [selectedProjectIds])

  const selectAllTargetIds = useMemo(
    () => projects.map((project) => project.projectId).filter(Boolean).slice(0, TEAM_TAB_PROJECTS_ORDER_MAX_SELECTED),
    [projects],
  )

  const isAllSelected = useMemo(
    () => (
      selectAllTargetIds.length > 0
      && selectedProjectIds.size === selectAllTargetIds.length
      && selectAllTargetIds.every((id) => selectedProjectIds.has(id))
    ),
    [selectAllTargetIds, selectedProjectIds],
  )

  const handleSelectAll = useCallback(() => {
    const isCurrentlyFull = (
      selectAllTargetIds.length > 0
      && selectedProjectIds.size === selectAllTargetIds.length
      && selectAllTargetIds.every((id) => selectedProjectIds.has(id))
    )

    if (isCurrentlyFull) {
      setSelectedProjectIds(new Set())
      setOrderedSelectedIds([])
      return
    }

    setSelectedProjectIds(new Set(selectAllTargetIds))
    setOrderedSelectedIds([...selectAllTargetIds])
  }, [selectAllTargetIds, selectedProjectIds])

  const onToggleProject = useCallback((projectId: string) => {
    setSelectedProjectIds((prev) => {
      const next = new Set(prev)
      if (next.has(projectId)) {
        next.delete(projectId)
        setOrderedSelectedIds((ordered) => ordered.filter((id) => id !== projectId))
      } else {
        if (next.size >= TEAM_TAB_PROJECTS_ORDER_MAX_SELECTED) {
          toasts.error(
            t('teams.adminPage.additionalFields.define_project_order_max_selected', {
              count: TEAM_TAB_PROJECTS_ORDER_MAX_SELECTED,
            }),
          )
          return prev
        }
        next.add(projectId)
        setOrderedSelectedIds((ordered) => (
          ordered.includes(projectId) ? ordered : [...ordered, projectId]
        ))
      }
      return next
    })
  }, [toasts, t])

  const showSaveCheckboxFirstToast = useCallback(() => {
    toasts.error(t('teams.adminPage.additionalFields.define_project_order_save_checkbox_first'))
  }, [toasts, t])

  const handleSave = useCallback(async () => {
    const projectSorting = orderedSelectedIds.filter((id) => selectedProjectIds.has(id))

    if (!teamId) {
      toasts.error('Не удалось определить организацию')
      return
    }

    setSaving(true)

    try {
      const teamRaw = await API.Json(TeamAPI.Get({ idTeam: teamId })) as any

      applyCustomSortingToTeamUpdate(
        teamRaw,
        projectSorting,
        entityType,
        isTabFilter,
        tabIndex,
        { organizationsCatalog: organizationsCatalogMode },
      )

      const updateResponse = await TeamAPI.Update(teamRaw)
      if (updateResponse?.status === 400) {
        showSaveCheckboxFirstToast()
        return
      }
      if (updateResponse?.status !== 200) {
        throw new Error('Team update failed')
      }

      const sortingResponse = await syncCustomSorting({
        entityType,
        teamId,
        itemsIds: projectSorting,
      })
      if (sortingResponse?.status === 400) {
        showSaveCheckboxFirstToast()
        return
      }
      if (!sortingResponse?.ok) {
        throw new Error(isOrganizationsEntityType(entityType)
          ? 'ChangeTeamSorting failed'
          : 'ChangeProjectSorting failed')
      }

      onSave(projectSorting)
      modal.hide()
    } catch {
      toasts.error('Не удалось сохранить порядок проектов')
    } finally {
      setSaving(false)
    }
  }, [
    orderedSelectedIds,
    selectedProjectIds,
    teamId,
    isTabFilter,
    tabIndex,
    organizationsCatalogMode,
    onSave,
    modal,
    toasts,
    entityType,
    showSaveCheckboxFirstToast,
  ])

  const handleBack = useCallback(() => {
    modal.hide()
  }, [modal])

  const projectsLoader = useMemo(() => (
    <TeamTabProjectsOrderGridSkeleton count={4} />
  ), [])

  const renderProjectsContent = () => {
    if (activeTab === 'all') {
      return (
        <>
          <TeamTabProjectsOrderGrid
            projects={projects}
            entityType={entityType}
            selectedProjectIds={selectedProjectIds}
            onToggleProject={onToggleProject}
            draggable={false}
          />
          {hasMoreProjects && <LoadMoreSentinel ref={loadMoreSentinelRef} />}
          {loadingMore && projectsLoader}
        </>
      )
    }

    if (!selectedProjects.length) {
      return (
        <EmptyState>
          {t('teams.adminPage.additionalFields.define_project_order_tab_selected_empty')}
        </EmptyState>
      )
    }

    return (
      <TeamTabProjectsOrderGrid
        projects={selectedProjects}
        entityType={entityType}
        selectedProjectIds={selectedProjectIds}
        onToggleProject={onToggleProject}
        onSortEnd={onSelectedProjectSort}
        draggable
      />
    )
  }

  return (
    <PageWrapper>
      <ContentWrapper>
        <TabPanelContainer>
          <ProjectMetaBack onBackClick={handleBack} />
          <FlexBox justify="space-between" align="center" alignMobile="flex-start" directionMobile="column" gyMobile={16}>
            <TabPanelHeader>{title}</TabPanelHeader>
            <BrgButton variant={ButtonVariant.Filled} disabled={saving} onClick={handleSave}>
              {t('teams.adminPage.simply.tabs.save')}
            </BrgButton>
          </FlexBox>
        </TabPanelContainer>

        {loading ? (
          <ScrollArea>
            <TeamTabProjectsOrderGridSkeleton count={8} />
          </ScrollArea>
        ) : !listQueryKey ? (
          <EmptyState>
            {isOrganizations ? 'Нет организаций для упорядочивания' : 'Нет проектов для упорядочивания'}
          </EmptyState>
        ) : (
          <>
            <TabsRow justify="space-between" align="center" alignMobile="flex-start" directionMobile="column" gyMobile={12}>
              <FlexBox gx={24} gxMobile={16}>
                <SwitcherItem
                  isActive={activeTab === 'all'}
                  onClick={() => setActiveTab('all')}
                >
                  {t('teams.adminPage.additionalFields.define_project_order_tab_all')}
                </SwitcherItem>
                <SwitcherItem
                  isActive={activeTab === 'selected'}
                  onClick={() => setActiveTab('selected')}
                >
                  {t('teams.adminPage.additionalFields.define_project_order_tab_selected')}
                  {selectedProjects.length ? ` (${selectedProjects.length})` : ''}
                </SwitcherItem>
              </FlexBox>
              {/*{activeTab === 'all' && projects.length > 0 ? (*/}
              {/*  <MagicBoxRow*/}
              {/*    onClick={handleSelectAll}*/}
              {/*    checked={isAllSelected}*/}
              {/*    label={t('competition.select_all')}*/}
              {/*  />*/}
              {/*) : null}*/}
            </TabsRow>

            <ScrollArea id={SCROLL_AREA_ID}>
              {activeTab === 'all' && !projects.length ? (
                <EmptyState>
                  {isOrganizations ? 'Нет организаций для упорядочивания' : 'Нет проектов для упорядочивания'}
                </EmptyState>
              ) : (
                renderProjectsContent()
              )}
            </ScrollArea>
          </>
        )}
      </ContentWrapper>
    </PageWrapper>
  )
}
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { BrgButton, ButtonVariant, Colors, FlexBox, MagicBoxRow, useToasts } from '@brg/ui'
import { API, Project, TeamAPI } from '@brg/api'
import { LayoutConst, useModal } from 'core'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import {
  fetchTeamTabProjectsForOrder,
  fetchTeamTabProjectsPageForOrder,
  OrganizationsListSource,
  TEAM_TAB_PROJECTS_ORDER_PAGE_LIMIT,
  TeamTabProjectsPagePaging,
} from 'shared/pages/teams/helpers/fetchTeamTabProjectsForOrder'
import { OrganizationsOrderGetListParams } from 'shared/pages/teams/helpers/buildOrganizationsOrderModalGetListParams'
import { applyCustomSortingToTeamUpdate } from 'shared/pages/teams/helpers/customSortingStorage'
import { syncCustomSorting } from 'shared/pages/teams/helpers/syncCustomSorting'
import { isOrganizationsEntityType } from 'shared/pages/teams/helpers/prepareCustomSortingGridFilter'
import {
  TeamTabProjectsOrderGrid,
  TeamTabProjectsOrderGridSkeleton,
} from 'shared/modals/teamTabProjectsOrder/TeamTabProjectsOrderGrid'
import { ProjectMetaBack } from 'shared/components/ProjectMetaBack/ProjectMetaBack'
import { TabPanelContainer, TabPanelHeader } from 'shared/pages/teams/components/Panel/styles'
import { SwitcherItem } from 'shared/components/ChannelsInfo/styles'
// import { useSortableDragAutoScroll } from 'shared/modals/teamTabProjectsOrder/useSortableDragAutoScroll'

type TeamTabProjectsOrderModalProps = {
  teamId: string
  contextNames: string[]
  organizationsGetListParams?: OrganizationsOrderGetListParams | null
  organizationsListSource?: OrganizationsListSource
  entityType?: string
  projectIds?: string[]
  tabPath?: string
  tabIndex?: number
  isTabFilter?: boolean
  tabName?: string
  organizationsCatalogMode?: boolean
  onSave: (orderedProjectIds: string[]) => void
}

type ModalViewTab = 'all' | 'selected'

const SCROLL_AREA_ID = 'team-tab-projects-order-scroll'
const TEAM_TAB_PROJECTS_ORDER_MAX_SELECTED = 60

const PageWrapper = styled.div`
  width: 100%;
  height: 100vh;
  background: #fefefe;
  padding: 40px 24px 24px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: ${LayoutConst.screen.s - 1}px) {
    padding: 24px 10px 16px;
  }
`

const ContentWrapper = styled.div`
  width: 100%;
  max-width: ${LayoutConst.container.l}px;
  margin: 0 auto;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`

const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  margin-top: 24px;
`

const TabsRow = styled(FlexBox)`
  margin-top: 24px;
  margin-bottom: 8px;
`

const EmptyState = styled.div`
  margin-top: 48px;
  font-size: 17px;
  line-height: 22px;
  color: ${Colors.galBlack47};
`

const LoadMoreSentinel = styled.div`
  width: 100%;
  height: 1px;
`

/** Модалка сортировки проектов вкладки. */
export const TeamTabProjectsOrderModal: FC<TeamTabProjectsOrderModalProps> = ({
  teamId,
  contextNames,
  organizationsGetListParams,
  organizationsListSource,
  entityType,
  projectIds = [],
  tabPath = '',
  tabIndex = 0,
  isTabFilter = true,
  tabName,
  organizationsCatalogMode = false,
  onSave,
}) => {
  const modal = useModal()
  const toasts = useToasts()
  const { t, i18n } = useTranslation()
  const language = i18n.language || 'ru'

  const [activeTab, setActiveTab] = useState<ModalViewTab>('all')
  const [projects, setProjects] = useState<Project[]>([])
  const [selectedProjectsLoaded, setSelectedProjectsLoaded] = useState<Project[]>([])
  const [selectedProjectIds, setSelectedProjectIds] = useState<Set<string>>(() => new Set())
  const [orderedSelectedIds, setOrderedSelectedIds] = useState<string[]>(() => [])
  const [effectiveProjectIds, setEffectiveProjectIds] = useState<string[]>(() => projectIds)
  const [paging, setPaging] = useState<TeamTabProjectsPagePaging | null>(null)
  const [hasReachedEnd, setHasReachedEnd] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [saving, setSaving] = useState(false)
  const loadMoreLockRef = useRef(false)
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null)
  const projectsCountRef = useRef(0)
  const isOrganizations = isOrganizationsEntityType(entityType)

  const contextNamesRef = useRef(contextNames)
  contextNamesRef.current = contextNames

  const organizationsGetListParamsRef = useRef(organizationsGetListParams)
  organizationsGetListParamsRef.current = organizationsGetListParams

  const organizationsListSourceRef = useRef(organizationsListSource)
  organizationsListSourceRef.current = organizationsListSource

  const toastsRef = useRef(toasts)
  toastsRef.current = toasts

  const listQueryKey = useMemo(() => {
    if (isOrganizations) {
      if (organizationsListSource === 'myAdminTeams') {
        return 'myAdminTeams'
      }
      const params = organizationsGetListParams
      if (!params?.contexts?.length) return ''
      return JSON.stringify({
        contexts: params.contexts,
        excludeModerationTypes: params.excludeModerationTypes,
      })
    }
    if (contextNames.length) {
      return contextNames.join(',')
    }
    if (teamId) {
      return `team:${teamId}`
    }
    return ''
  }, [
    isOrganizations,
    organizationsListSource,
    contextNames.join(','),
    teamId,
    organizationsGetListParams?.contexts?.join(','),
    organizationsGetListParams?.excludeModerationTypes?.join(','),
  ])

  useEffect(() => {
    setActiveTab('all')
  }, [tabPath, tabIndex, teamId])

  const title = useMemo(() => {
    const name = tabName || t('teams.adminPage.buttons.empty')
    if (isOrganizations) {
      return `Сортировка организаций на вкладке «${name}»`
    }
    return `Сортировка проектов на вкладке «${name}»`
  }, [tabName, t, isOrganizations])

  const projectsMap = useMemo(() => {
    const map = new Map<string, Project>()

    selectedProjectsLoaded.forEach((project) => {
      map.set(project.projectId, project)
    })

    projects.forEach((project) => {
      if (!map.has(project.projectId)) {
        map.set(project.projectId, project)
      }
    })

    return map
  }, [selectedProjectsLoaded, projects])

  const selectedProjects = useMemo(
    () => orderedSelectedIds
      .filter((id) => selectedProjectIds.has(id))
      .map((id) => projectsMap.get(id))
      .filter(Boolean) as Project[],
    [orderedSelectedIds, selectedProjectIds, projectsMap],
  )

  // const isDragSortActive = activeTab === 'selected' && selectedProjects.length > 0
  // useSortableDragAutoScroll(isDragSortActive ? SCROLL_AREA_ID : undefined)

  const hasMoreProjects = useMemo(() => {
    if (hasReachedEnd || loading || !paging) return false
    if (paging.pages <= 0) return false
    return paging.page < paging.pages
  }, [hasReachedEnd, loading, paging])

  useEffect(() => {
    projectsCountRef.current = projects.length
  }, [projects.length])

  const projectIdsKey = (projectIds || []).filter(Boolean).join(',')

  useEffect(() => {
    const ids = (projectIds || []).filter(Boolean)
    setEffectiveProjectIds(ids)
    setOrderedSelectedIds(ids)
    setSelectedProjectIds(new Set(ids))
  }, [projectIdsKey, tabPath, tabIndex])

  useEffect(() => {
    if (!listQueryKey) {
      setProjects([])
      setPaging(null)
      setSelectedProjectsLoaded([])
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)
    setProjects([])
    setPaging(null)
    setHasReachedEnd(false)
    loadMoreLockRef.current = false

    fetchTeamTabProjectsPageForOrder({
      contextNames: contextNamesRef.current,
      teamId,
      organizationsGetListParams: organizationsGetListParamsRef.current ?? undefined,
      organizationsListSource: organizationsListSourceRef.current,
      entityType,
      language,
      limit: TEAM_TAB_PROJECTS_ORDER_PAGE_LIMIT,
      offset: 0,
    })
      .then(({ projects: loadedProjects, paging: loadedPaging }) => {
        if (cancelled) return
        setProjects(loadedProjects)
        setPaging(loadedPaging)
        if (
          loadedProjects.length === 0
          || loadedProjects.length < TEAM_TAB_PROJECTS_ORDER_PAGE_LIMIT
          || loadedPaging.page >= loadedPaging.pages
        ) {
          setHasReachedEnd(true)
        }
      })
      .catch(() => {
        if (!cancelled) {
          toastsRef.current.error('Не удалось загрузить проекты')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [listQueryKey, entityType, language])

  const loadMoreProjects = useCallback(async () => {
    if (
      loadMoreLockRef.current
      || !listQueryKey
      || loading
      || loadingMore
      || hasReachedEnd
      || !paging
      || paging.page >= paging.pages
    ) {
      return
    }

    loadMoreLockRef.current = true
    setLoadingMore(true)

    try {
      const offset = projectsCountRef.current
      const { projects: loadedProjects, paging: loadedPaging } = await fetchTeamTabProjectsPageForOrder({
        contextNames: contextNamesRef.current,
        teamId,
        organizationsGetListParams: organizationsGetListParamsRef.current ?? undefined,
        organizationsListSource: organizationsListSourceRef.current,
        entityType,
        language,
        limit: TEAM_TAB_PROJECTS_ORDER_PAGE_LIMIT,
        offset,
      })

      if (!loadedProjects.length) {
        setHasReachedEnd(true)
        return
      }

      setProjects((prev) => [...prev, ...loadedProjects])
      setPaging(loadedPaging)

      if (
        loadedProjects.length < TEAM_TAB_PROJECTS_ORDER_PAGE_LIMIT
        || loadedPaging.page >= loadedPaging.pages
      ) {
        setHasReachedEnd(true)
      }
    } catch {
      toastsRef.current.error('Не удалось загрузить проекты')
    } finally {
      loadMoreLockRef.current = false
      setLoadingMore(false)
    }
  }, [listQueryKey, entityType, language, loading, loadingMore, hasReachedEnd, paging])

  const loadMoreProjectsRef = useRef(loadMoreProjects)
  loadMoreProjectsRef.current = loadMoreProjects

  useEffect(() => {
    if (activeTab !== 'all' || !hasMoreProjects || loading || loadingMore || !projects.length) return

    const scrollRoot = document.getElementById(SCROLL_AREA_ID)
    const sentinel = loadMoreSentinelRef.current
    if (!scrollRoot || !sentinel) return

    if (scrollRoot.scrollHeight <= scrollRoot.clientHeight + 50) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMoreProjectsRef.current()
        }
      },
      { root: scrollRoot, rootMargin: '80px', threshold: 0 },
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [activeTab, hasMoreProjects, loading, loadingMore, projects.length])

  const effectiveProjectIdsKey = effectiveProjectIds.join(',')

  useEffect(() => {
    if (!listQueryKey) {
      setSelectedProjectsLoaded([])
      return
    }

    if (!effectiveProjectIds.length) {
      setSelectedProjectsLoaded([])
      return
    }

    let cancelled = false

    fetchTeamTabProjectsForOrder({
      contextNames: contextNamesRef.current,
      teamId,
      organizationsGetListParams: organizationsGetListParamsRef.current ?? undefined,
      organizationsListSource: organizationsListSourceRef.current,
      entityType,
      projectIds: effectiveProjectIds,
      language,
    })
      .then((loadedProjects) => {
        if (cancelled) return
        setSelectedProjectsLoaded(loadedProjects)
      })
      .catch(() => {
        if (!cancelled) setSelectedProjectsLoaded([])
      })

    return () => {
      cancelled = true
    }
  }, [listQueryKey, entityType, language, effectiveProjectIdsKey])

  const onSelectedProjectSort = useCallback((projectId: string, newIndex: number) => {
    setOrderedSelectedIds((prev) => {
      const visibleIds = prev.filter((id) => selectedProjectIds.has(id))
      const oldIndex = visibleIds.indexOf(projectId)
      if (oldIndex === -1 || oldIndex === newIndex) return prev

      const nextVisibleIds = [...visibleIds]
      const [moved] = nextVisibleIds.splice(oldIndex, 1)
      nextVisibleIds.splice(newIndex, 0, moved)

      const visibleSet = new Set(visibleIds)
      const next = [...prev]
      let visiblePointer = 0

      for (let index = 0; index < next.length; index += 1) {
        if (visibleSet.has(next[index])) {
          next[index] = nextVisibleIds[visiblePointer]
          visiblePointer += 1
        }
      }

      return next
    })
  }, [selectedProjectIds])

  const selectAllTargetIds = useMemo(
    () => projects.map((project) => project.projectId).filter(Boolean).slice(0, TEAM_TAB_PROJECTS_ORDER_MAX_SELECTED),
    [projects],
  )

  const isAllSelected = useMemo(
    () => (
      selectAllTargetIds.length > 0
      && selectedProjectIds.size === selectAllTargetIds.length
      && selectAllTargetIds.every((id) => selectedProjectIds.has(id))
    ),
    [selectAllTargetIds, selectedProjectIds],
  )

  const handleSelectAll = useCallback(() => {
    const isCurrentlyFull = (
      selectAllTargetIds.length > 0
      && selectedProjectIds.size === selectAllTargetIds.length
      && selectAllTargetIds.every((id) => selectedProjectIds.has(id))
    )

    if (isCurrentlyFull) {
      setSelectedProjectIds(new Set())
      setOrderedSelectedIds([])
      return
    }

    setSelectedProjectIds(new Set(selectAllTargetIds))
    setOrderedSelectedIds([...selectAllTargetIds])
  }, [selectAllTargetIds, selectedProjectIds])

  const onToggleProject = useCallback((projectId: string) => {
    setSelectedProjectIds((prev) => {
      const next = new Set(prev)
      if (next.has(projectId)) {
        next.delete(projectId)
        setOrderedSelectedIds((ordered) => ordered.filter((id) => id !== projectId))
      } else {
        if (next.size >= TEAM_TAB_PROJECTS_ORDER_MAX_SELECTED) {
          toasts.error(
            t('teams.adminPage.additionalFields.define_project_order_max_selected', {
              count: TEAM_TAB_PROJECTS_ORDER_MAX_SELECTED,
            }),
          )
          return prev
        }
        next.add(projectId)
        setOrderedSelectedIds((ordered) => (
          ordered.includes(projectId) ? ordered : [...ordered, projectId]
        ))
      }
      return next
    })
  }, [toasts, t])

  const showSaveCheckboxFirstToast = useCallback(() => {
    toasts.error(t('teams.adminPage.additionalFields.define_project_order_save_checkbox_first'))
  }, [toasts, t])

  const handleSave = useCallback(async () => {
    const projectSorting = orderedSelectedIds.filter((id) => selectedProjectIds.has(id))

    if (!teamId) {
      toasts.error('Не удалось определить организацию')
      return
    }

    setSaving(true)

    try {
      const teamRaw = await API.Json(TeamAPI.Get({ idTeam: teamId })) as any

      applyCustomSortingToTeamUpdate(
        teamRaw,
        projectSorting,
        entityType,
        isTabFilter,
        tabIndex,
        { organizationsCatalog: organizationsCatalogMode },
      )

      const updateResponse = await TeamAPI.Update(teamRaw)
      if (updateResponse?.status === 400) {
        showSaveCheckboxFirstToast()
        return
      }
      if (updateResponse?.status !== 200) {
        throw new Error('Team update failed')
      }

      const sortingResponse = await syncCustomSorting({
        entityType,
        teamId,
        itemsIds: projectSorting,
      })
      if (sortingResponse?.status === 400) {
        showSaveCheckboxFirstToast()
        return
      }
      if (!sortingResponse?.ok) {
        throw new Error(isOrganizationsEntityType(entityType)
          ? 'ChangeTeamSorting failed'
          : 'ChangeProjectSorting failed')
      }

      onSave(projectSorting)
      modal.hide()
    } catch {
      toasts.error('Не удалось сохранить порядок проектов')
    } finally {
      setSaving(false)
    }
  }, [
    orderedSelectedIds,
    selectedProjectIds,
    teamId,
    isTabFilter,
    tabIndex,
    organizationsCatalogMode,
    onSave,
    modal,
    toasts,
    entityType,
    showSaveCheckboxFirstToast,
  ])

  const handleBack = useCallback(() => {
    modal.hide()
  }, [modal])

  const projectsLoader = useMemo(() => (
    <TeamTabProjectsOrderGridSkeleton count={4} />
  ), [])

  const renderProjectsContent = () => {
    if (activeTab === 'all') {
      return (
        <>
          <TeamTabProjectsOrderGrid
            projects={projects}
            entityType={entityType}
            selectedProjectIds={selectedProjectIds}
            onToggleProject={onToggleProject}
            draggable={false}
          />
          {hasMoreProjects && <LoadMoreSentinel ref={loadMoreSentinelRef} />}
          {loadingMore && projectsLoader}
        </>
      )
    }

    if (!selectedProjects.length) {
      return (
        <EmptyState>
          {t('teams.adminPage.additionalFields.define_project_order_tab_selected_empty')}
        </EmptyState>
      )
    }

    return (
      <TeamTabProjectsOrderGrid
        projects={selectedProjects}
        entityType={entityType}
        selectedProjectIds={selectedProjectIds}
        onToggleProject={onToggleProject}
        onSortEnd={onSelectedProjectSort}
        draggable
      />
    )
  }

  return (
    <PageWrapper>
      <ContentWrapper>
        <TabPanelContainer>
          <ProjectMetaBack onBackClick={handleBack} />
          <FlexBox justify="space-between" align="center" alignMobile="flex-start" directionMobile="column" gyMobile={16}>
            <TabPanelHeader>{title}</TabPanelHeader>
            <BrgButton variant={ButtonVariant.Filled} disabled={saving} onClick={handleSave}>
              {t('teams.adminPage.simply.tabs.save')}
            </BrgButton>
          </FlexBox>
        </TabPanelContainer>

        {loading ? (
          <ScrollArea>
            <TeamTabProjectsOrderGridSkeleton count={8} />
          </ScrollArea>
        ) : !listQueryKey ? (
          <EmptyState>
            {isOrganizations ? 'Нет организаций для упорядочивания' : 'Нет проектов для упорядочивания'}
          </EmptyState>
        ) : (
          <>
            <TabsRow justify="space-between" align="center" alignMobile="flex-start" directionMobile="column" gyMobile={12}>
              <FlexBox gx={24} gxMobile={16}>
                <SwitcherItem
                  isActive={activeTab === 'all'}
                  onClick={() => setActiveTab('all')}
                >
                  {t('teams.adminPage.additionalFields.define_project_order_tab_all')}
                </SwitcherItem>
                <SwitcherItem
                  isActive={activeTab === 'selected'}
                  onClick={() => setActiveTab('selected')}
                >
                  {t('teams.adminPage.additionalFields.define_project_order_tab_selected')}
                  {selectedProjects.length ? ` (${selectedProjects.length})` : ''}
                </SwitcherItem>
              </FlexBox>
              {/*{activeTab === 'all' && projects.length > 0 ? (*/}
              {/*  <MagicBoxRow*/}
              {/*    onClick={handleSelectAll}*/}
              {/*    checked={isAllSelected}*/}
              {/*    label={t('competition.select_all')}*/}
              {/*  />*/}
              {/*) : null}*/}
            </TabsRow>

            <ScrollArea id={SCROLL_AREA_ID}>
              {activeTab === 'all' && !projects.length ? (
                <EmptyState>
                  {isOrganizations ? 'Нет организаций для упорядочивания' : 'Нет проектов для упорядочивания'}
                </EmptyState>
              ) : (
                renderProjectsContent()
              )}
            </ScrollArea>
          </>
        )}
      </ContentWrapper>
    </PageWrapper>
  )
}
